import { formatDate } from 'common/helpers/v1/dates';
import { AlertTriggers, CartPriceChangeAlert } from 'types/machine-health/alerts';

export interface OrganizedAlerts {
  today: AlertTriggers[];
  yesterday: AlertTriggers[];
  older: AlertTriggers[];
}

export const getNotificationSourceDate = (
  notification: Pick<AlertTriggers, 'timestamp' | 'createdAt' | 'start'>
): string | undefined => notification.timestamp || notification.createdAt || notification.start;

export const getNotificationId = (
  notification?: Pick<AlertTriggers, 'id' | 'alertId'>
): string | undefined => notification?.id || notification?.alertId;

export const getNotificationOrderNumber = (
  customData?: CartPriceChangeAlert
): string | undefined => customData?.orderId || customData?.omnibluOrderNum?.toString();

export const getElapsedMinutes = (date1: Date, date2: Date): number =>
  Math.max(Math.floor((date1.getTime() - date2.getTime()) / (1000 * 60)), 0);

const formatElapsedTime = (minutesAgo: number): string => {
  if (minutesAgo < 60) {
    return `${minutesAgo} Minute${minutesAgo === 1 ? '' : 's'} ago`;
  }

  const hoursAgo = Math.floor(minutesAgo / 60);
  return `${hoursAgo} Hour${hoursAgo === 1 ? '' : 's'} ago`;
};

const formatNotificationDate = (itemDate: Date, timeZone?: string | null): string =>
  ` ${formatDate(itemDate, 'hours-minutes-seconds', timeZone)} on ${formatDate(
    itemDate,
    'long',
    timeZone
  )}`;

export const organizeNotificationsByDate = (
  items: AlertTriggers[],
  timeZone?: string | null,
  currentDate = new Date()
): OrganizedAlerts => {
  const yesterday = new Date(currentDate);
  yesterday.setDate(yesterday.getDate() - 1);
  const todayDateLabel = formatDate(currentDate, 'long', timeZone);
  const yesterdayDateLabel = formatDate(yesterday, 'long', timeZone);

  return items.reduce<OrganizedAlerts>(
    (acc, item) => {
      const itemTimestamp = getNotificationSourceDate(item);

      if (!itemTimestamp) {
        acc.older.push(item);
        return acc;
      }

      const itemDate = new Date(itemTimestamp);

      if (Number.isNaN(itemDate.getTime())) {
        acc.older.push(item);
        return acc;
      }

      const itemDateLabel = formatDate(itemDate, 'long', timeZone);

      if (itemDateLabel === todayDateLabel) {
        const minutesAgo = getElapsedMinutes(currentDate, itemDate);
        acc.today.push({ ...item, start: formatElapsedTime(minutesAgo) });
      } else if (itemDateLabel === yesterdayDateLabel) {
        acc.yesterday.push({ ...item, start: formatNotificationDate(itemDate, timeZone) });
      } else {
        acc.older.push({ ...item, start: formatNotificationDate(itemDate, timeZone) });
      }

      return acc;
    },
    { today: [], yesterday: [], older: [] }
  );
};
